import React from 'react';

interface Props {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: Error | null;
}

// App-level error boundary. If the React tree throws inside DataProvider or
// any view, this catches it and renders a recovery panel instead of a blank
// screen. Without this, a single bad render unmounts the whole SPA.
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught error', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div className="h-full grid place-items-center p-8">
          <div className="panel max-w-xl p-6 space-y-4 border-red/60">
            <div className="text-red font-mono text-xs tracking-widest uppercase">
              Something broke
            </div>
            <h2 className="text-xl font-semibold">Unexpected error</h2>
            <p className="text-sm text-muted font-mono whitespace-pre-wrap break-words">
              {this.state.error.message}
            </p>
            <details className="text-xs text-dim font-mono">
              <summary className="cursor-pointer">Stack trace</summary>
              <pre className="mt-2 overflow-auto max-h-[30vh] text-[10px]">
                {this.state.error.stack}
              </pre>
            </details>
            <div className="flex gap-2">
              <button onClick={this.reset} className="btn-primary text-xs">
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn text-xs"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
